import { useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';
import { APP } from '@/constants/testIds';
import { toast } from 'sonner';
import { Home, MapPin, Trees, Plus, Crosshair, Satellite } from 'lucide-react';
import FarmMap from '@/components/FarmMap';
import LandCoverGate from '@/components/LandCoverGate';

const DEFAULT_FARM = {
  name: '', lat: 17.9784, lon: 79.5941, area_ha: '', soil_type: '', irrigation_availability: 'rainfed',
};

const FarmerFarmSelector = ({
  farmers, farms, activeFarmer, activeFarm,
  setActiveFarmerId, setActiveFarmId,
  onFarmerCreated, onFarmCreated,
}) => {
  const [showFarmerForm, setShowFarmerForm] = useState(false);
  const [showFarmForm, setShowFarmForm] = useState(false);
  const [farmerForm, setFarmerForm] = useState({ name: '', phone: '', village: '', mandal: '', district: '' });
  const [farmForm, setFarmForm] = useState(DEFAULT_FARM);
  const [saving, setSaving] = useState(false);

  const [ring, setRing] = useState(null);          // polygon ring drawn on map
  const [lcResult, setLcResult] = useState(null);   // land-cover classification result
  const [lcLoading, setLcLoading] = useState(false);

  const submitFarmer = async () => {
    if (!farmerForm.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const f = await api.createFarmer(farmerForm);
      await onFarmerCreated();
      setActiveFarmerId(f.id);
      setShowFarmerForm(false);
      setFarmerForm({ name: '', phone: '', village: '', mandal: '', district: '' });
      toast.success(`Registered ${f.name}`);
    } catch (e) { toast.error('Failed to register farmer'); } finally { setSaving(false); }
  };

  const runLandCover = async () => {
    if (!ring || ring.length < 3) {
      toast.error('Draw a polygon around your field on the map first.');
      return;
    }
    setLcLoading(true); setLcResult(null);
    try {
      const r = await api.landCover({ ring, target_crop: 'Rice' });
      setLcResult(r);
      // auto-update lat/lon and area from centroid + measured area
      setFarmForm(f => ({
        ...f,
        lat: r.parcel.centroid.lat,
        lon: r.parcel.centroid.lon,
        area_ha: r.parcel.area_ha,
      }));
      if (r.decision !== 'PROCEED') toast.warning('Not a cropped area — pick another parcel.');
    } catch (e) { toast.error('Land-cover classification failed'); }
    finally { setLcLoading(false); }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error('Geolocation not available in this browser'); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => setFarmForm(f => ({ ...f, lat: pos.coords.latitude, lon: pos.coords.longitude })),
      () => toast.error('Could not read your location'),
      { enableHighAccuracy: true, timeout: 8000 },
    );
  };

  const submitFarm = async () => {
    if (!farmForm.name.trim()) { toast.error('Farm name is required'); return; }
    if (!activeFarmer) return;
    if (!lcResult || lcResult.decision !== 'PROCEED') {
      toast.error('Please draw a polygon and get a confirmed "cropped parcel" verdict before saving.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        farmer_id: activeFarmer.id,
        name: farmForm.name,
        lat: parseFloat(farmForm.lat), lon: parseFloat(farmForm.lon),
        area_ha: farmForm.area_ha ? parseFloat(farmForm.area_ha) : null,
        soil_type: farmForm.soil_type || null,
        irrigation_availability: farmForm.irrigation_availability,
        parcel_ring: ring,
        land_cover: lcResult,
      };
      const fm = await api.createFarm(payload);
      await onFarmCreated();
      setActiveFarmId(fm.id);
      setShowFarmForm(false);
      setRing(null); setLcResult(null); setFarmForm(DEFAULT_FARM);
      toast.success(`Farm '${fm.name}' registered`);
    } catch (e) { toast.error('Failed to register farm'); } finally { setSaving(false); }
  };

  const activeFarmerLocation = useMemo(
    () => [activeFarmer?.village, activeFarmer?.mandal, activeFarmer?.district].filter(Boolean).join(', '),
    [activeFarmer?.village, activeFarmer?.mandal, activeFarmer?.district],
  );

  const mapCenter = [parseFloat(farmForm.lat) || 17.9784, parseFloat(farmForm.lon) || 79.5941];

  return (
    <div className="grid gap-4 md:grid-cols-2 mb-6 animate-fade-up">
      {/* Farmer card */}
      <Card>
        <CardContent className="pt-5 space-y-3">
          <div className="flex items-center gap-2">
            <Home className="h-5 w-5 text-icrisat-green" />
            <h3 className="font-display text-lg text-icrisat-green">Farmer</h3>
          </div>
          <Select value={activeFarmer?.id || ''} onValueChange={setActiveFarmerId}>
            <SelectTrigger data-testid={APP.farmerSelect}><SelectValue placeholder="Select farmer" /></SelectTrigger>
            <SelectContent>
              {farmers.map(f => (
                <SelectItem key={f.id} value={f.id}>{f.name}{f.village ? ` · ${f.village}` : ''}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {activeFarmer && (
            <p className="text-sm text-icrisat-earth">
              <span className="font-medium text-icrisat-green">{activeFarmer.name}</span>
              {activeFarmerLocation && <> · 📍 {activeFarmerLocation}</>}
              {activeFarmer.phone && <> · 📞 {activeFarmer.phone}</>}
            </p>
          )}
          <Button
            variant="outline" size="sm"
            data-testid={APP.registerFarmerBtn}
            onClick={() => setShowFarmerForm(v => !v)}
            className="border-icrisat-green text-icrisat-green hover:bg-icrisat-green hover:text-white"
          >
            <Plus className="h-4 w-4 mr-1" />{showFarmerForm ? 'Close' : 'Register another farmer'}
          </Button>
          {showFarmerForm && (
            <div className="grid grid-cols-2 gap-3 pt-3 border-t border-border/60">
              <Input data-testid={APP.farmerForm.name} placeholder="Name *" value={farmerForm.name} onChange={e => setFarmerForm({ ...farmerForm, name: e.target.value })} />
              <Input data-testid={APP.farmerForm.phone} placeholder="Phone" value={farmerForm.phone} onChange={e => setFarmerForm({ ...farmerForm, phone: e.target.value })} />
              <Input data-testid={APP.farmerForm.village} placeholder="Village" value={farmerForm.village} onChange={e => setFarmerForm({ ...farmerForm, village: e.target.value })} />
              <Input data-testid={APP.farmerForm.mandal} placeholder="Mandal" value={farmerForm.mandal} onChange={e => setFarmerForm({ ...farmerForm, mandal: e.target.value })} />
              <Input className="col-span-2" data-testid={APP.farmerForm.district} placeholder="District" value={farmerForm.district} onChange={e => setFarmerForm({ ...farmerForm, district: e.target.value })} />
              <div className="col-span-2 flex justify-end gap-2">
                <Button variant="ghost" data-testid={APP.farmerForm.cancel} onClick={() => setShowFarmerForm(false)}>Cancel</Button>
                <Button data-testid={APP.farmerForm.submit} onClick={submitFarmer} disabled={saving} className="bg-icrisat-green hover:bg-[hsl(var(--icrisat-green-hover))] text-white">{saving ? '…' : 'Save'}</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Farm card */}
      <Card className="md:col-span-1">
        <CardContent className="pt-5 space-y-3">
          <div className="flex items-center gap-2">
            <Trees className="h-5 w-5 text-icrisat-green" />
            <h3 className="font-display text-lg text-icrisat-green">Farm</h3>
          </div>
          <Select value={activeFarm?.id || ''} onValueChange={setActiveFarmId}>
            <SelectTrigger data-testid={APP.farmSelect}>
              <SelectValue placeholder={farms.length ? 'Select farm' : 'No farms yet — register one'} />
            </SelectTrigger>
            <SelectContent>
              {farms.map(f => (
                <SelectItem key={f.id} value={f.id}>{f.name} · {f.lat.toFixed(3)}, {f.lon.toFixed(3)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {activeFarm && (
            <div className="text-sm text-icrisat-earth flex flex-wrap gap-3">
              <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {activeFarm.lat.toFixed(4)}, {activeFarm.lon.toFixed(4)}</span>
              {activeFarm.area_ha && <span>· {activeFarm.area_ha} ha</span>}
              <span>· {activeFarm.irrigation_availability.replace('_', ' ')}</span>
            </div>
          )}
          <Button
            variant="outline" size="sm"
            data-testid={APP.registerFarmBtn}
            onClick={() => setShowFarmForm(v => !v)}
            className="border-icrisat-green text-icrisat-green hover:bg-icrisat-green hover:text-white"
          >
            <Plus className="h-4 w-4 mr-1" />{showFarmForm ? 'Close' : 'Register new farm'}
          </Button>
        </CardContent>
      </Card>

      {/* Full-width farm registration flow */}
      {showFarmForm && (
        <Card className="md:col-span-2 border-icrisat-green/30">
          <CardContent className="pt-5 space-y-4">
            <div className="flex items-center gap-2">
              <Satellite className="h-5 w-5 text-icrisat-green" />
              <h3 className="font-display text-lg text-icrisat-green">Draw your field on the satellite map</h3>
              <span className="text-xs text-muted-foreground ml-auto hidden sm:inline">Google satellite imagery</span>
            </div>

            <FarmMap
              center={mapCenter}
              onPolygon={(r) => { setRing(r); setLcResult(null); }}
            />

            <div className="flex flex-wrap items-center gap-2">
              <Button
                data-testid="run-landcover-btn"
                onClick={runLandCover}
                disabled={!ring || lcLoading}
                className="bg-icrisat-green hover:bg-[hsl(var(--icrisat-green-hover))] text-white"
              >
                <Satellite className="h-4 w-4 mr-1" />
                {lcLoading ? 'Classifying…' : 'Check land cover'}
              </Button>
              <Button variant="outline" size="sm" onClick={useMyLocation} data-testid="use-my-location-btn">
                <Crosshair className="h-4 w-4 mr-1" /> Use my location
              </Button>
              <span className="text-xs text-muted-foreground">
                {ring ? `${ring.length - 1} vertices drawn` : 'Use the polygon tool in the top-right to trace your field boundary.'}
              </span>
            </div>

            <LandCoverGate loading={lcLoading} result={lcResult} />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3 border-t border-border/60">
              <Input data-testid={APP.farmForm.name} placeholder="Farm name *" className="sm:col-span-2" value={farmForm.name} onChange={e => setFarmForm({ ...farmForm, name: e.target.value })} />
              <div>
                <Label className="text-xs">Latitude</Label>
                <Input data-testid={APP.farmForm.lat} value={farmForm.lat} onChange={e => setFarmForm({ ...farmForm, lat: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Longitude</Label>
                <Input data-testid={APP.farmForm.lon} value={farmForm.lon} onChange={e => setFarmForm({ ...farmForm, lon: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Area (ha) — auto-filled from map</Label>
                <Input data-testid={APP.farmForm.area} value={farmForm.area_ha} onChange={e => setFarmForm({ ...farmForm, area_ha: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Soil type</Label>
                <Input data-testid={APP.farmForm.soil} value={farmForm.soil_type} onChange={e => setFarmForm({ ...farmForm, soil_type: e.target.value })} placeholder="e.g. Red sandy loam" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">Irrigation availability</Label>
                <Select value={farmForm.irrigation_availability} onValueChange={v => setFarmForm({ ...farmForm, irrigation_availability: v })}>
                  <SelectTrigger data-testid={APP.farmForm.irrigation}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rainfed">Rainfed</SelectItem>
                    <SelectItem value="supplemental">Supplemental (life-saving)</SelectItem>
                    <SelectItem value="full_season">Full season (assured)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
                <Button variant="ghost" data-testid={APP.farmForm.cancel} onClick={() => setShowFarmForm(false)}>Cancel</Button>
                <Button
                  data-testid={APP.farmForm.submit}
                  onClick={submitFarm}
                  disabled={saving || !lcResult || lcResult.decision !== 'PROCEED'}
                  className="bg-icrisat-green hover:bg-[hsl(var(--icrisat-green-hover))] text-white"
                >
                  {saving ? '…' : 'Save farm'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default FarmerFarmSelector;
